/**
 * Growth — Cart Abandonment. Stage 6.
 * Sources `/api/bi/growth/abandonment/`.
 */
import { useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { ShoppingCart, Sparkles } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { DataView } from '@/components/data/DataView';
import { KPICard } from '@/components/charts/KPICard';
import { Sparkline } from '@/components/charts/Sparkline';
import { useCartAbandonment, type AbandonmentStoreRow } from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

export function AbandonmentPage() {
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );

  const query = useCartAbandonment({ start: startISO, end: endISO, storeIds });
  const data = query.data;
  const funnel = data?.funnel;
  const trend = useMemo(
    () => (data?.daily ?? []).map((d) => ({ value: d.abandonment_rate * 100 })),
    [data?.daily],
  );

  const columns = useMemo<ColumnDef<AbandonmentStoreRow, unknown>[]>(
    () => [
      {
        id: 'store',
        header: 'Store',
        cell: ({ row }) => (
          <div className="text-sm font-medium">
            {row.original.store__name ?? `#${row.original.store_id ?? '—'}`}
          </div>
        ),
      },
      {
        id: 'created',
        header: () => <div className="text-right">Carts</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.number(row.original.created_n)}</div>
        ),
      },
      {
        id: 'with_items',
        header: () => <div className="text-right">With items</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.number(row.original.with_items_n)}</div>
        ),
      },
      {
        id: 'ordered',
        header: () => <div className="text-right">Ordered</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.number(row.original.ordered_n)}</div>
        ),
      },
      {
        id: 'abandoned',
        header: () => <div className="text-right">Abandoned</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm font-medium text-danger">
            {fmt.number(row.original.abandoned_n)}
          </div>
        ),
      },
      {
        id: 'rate',
        header: () => <div className="text-right">Rate</div>,
        cell: ({ row }) => {
          const r = row.original.abandonment_rate;
          const tone = r >= 0.6 ? 'danger' : r >= 0.35 ? 'warning' : 'success';
          return (
            <div className="text-right">
              <Badge tone={tone}>{fmt.pct(r)}</Badge>
            </div>
          );
        },
      },
    ],
    [],
  );

  const stages = funnel
    ? [
        { label: 'Carts created', value: funnel.created, tone: 'info' as const },
        { label: 'With items', value: funnel.with_items, tone: 'primary' as const },
        { label: 'Ordered', value: funnel.ordered, tone: 'success' as const },
      ]
    : [];
  const stagesMax = Math.max(1, ...stages.map((s) => s.value));

  return (
    <>
      <PageHeader
        eyebrow="Growth"
        title="Cart abandonment"
        description="Where carts go to die. The funnel is the obvious story; the per-store table is where you'll find the fix."
        actions={
          <Badge tone="info" className="gap-1">
            <Sparkles className="h-3 w-3" /> Stage 6
          </Badge>
        }
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Carts created"
          value={fmt.number(funnel?.created ?? 0)}
          loading={query.isLoading}
        />
        <KPICard
          label="Conversion"
          value={fmt.pct(funnel?.conversion_rate ?? 0)}
          loading={query.isLoading}
          hint="Ordered ÷ With items."
        />
        <KPICard
          label="Abandonment"
          value={fmt.pct(funnel?.abandonment_rate ?? 0)}
          goodWhenUp={false}
          loading={query.isLoading}
          trend={trend}
        />
        <KPICard
          label="Recoverable"
          value={fmt.npr(funnel?.recoverable_value ?? 0)}
          loading={query.isLoading}
          hint={`Avg abandoned cart: ${fmt.npr(funnel?.avg_abandoned_value ?? 0)}`}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardBody>
            <div className="mb-3 flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-display text-base font-semibold">Funnel</h3>
            </div>
            {query.isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-9 animate-pulse rounded-md bg-muted/40" />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {stages.map((s, idx) => {
                  const widthPct = Math.max(8, Math.round((s.value / stagesMax) * 100));
                  const dropFromPrev =
                    idx > 0 && stages[idx - 1].value > 0
                      ? 1 - s.value / stages[idx - 1].value
                      : null;
                  return (
                    <div key={s.label}>
                      <div className="mb-1 flex items-baseline justify-between text-xs">
                        <span className="font-medium text-foreground">{s.label}</span>
                        <span className="text-muted-foreground">
                          <span className="kpi-num text-foreground">{fmt.number(s.value)}</span>
                          {dropFromPrev != null ? (
                            <span className="ml-2 text-danger">
                              −{fmt.pct(dropFromPrev)}
                            </span>
                          ) : null}
                        </span>
                      </div>
                      <div className="h-3 overflow-hidden rounded-full bg-muted/40">
                        <div
                          className={cn(
                            'h-full rounded-full transition-[width] duration-500 ease-out',
                            s.tone === 'info' && 'bg-info',
                            s.tone === 'primary' && 'bg-primary',
                            s.tone === 'success' && 'bg-success',
                          )}
                          style={{ width: `${widthPct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h3 className="font-display text-base font-semibold">Daily abandonment %</h3>
            <p className="text-xs text-muted-foreground">
              Lower is better. Spikes usually mean a UX or stock issue.
            </p>
            <div className="mt-3 h-24">
              {trend.length ? (
                <Sparkline data={trend} tone="danger" height={96} />
              ) : (
                <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                  No data
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        {query.isError ? (
          <ErrorState
            description="Couldn't load abandonment data."
            onRetry={() => void query.refetch()}
          />
        ) : (
          <DataView<AbandonmentStoreRow>
            title="By store"
            data={data?.stores ?? []}
            columns={columns}
            isLoading={query.isLoading}
            emptyTitle="No carts in this window"
          />
        )}
      </div>
    </>
  );
}
